"use client"

import { useRef, useState } from "react"
import { formatDistanceToNow } from "date-fns"
import { cn } from "@/lib/utils"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Check, X, Edit, Mail, Clock, ChevronDown, ChevronRight, Loader2 } from "lucide-react"
import {
  getGetTouchpointsDueQueryKey,
  useGetTouchpointsDue,
  usePostTouchpointsTouchpointIdApprove,
} from "openapi/api/endpoints"
import { GetTouchpointsDue200Item } from "openapi/api/model"
import { useForm } from "react-hook-form"
import { Label } from "@/components/ui/label"
import { useQueryClient } from "@tanstack/react-query"

type TouchpointFormData = {
  subject: string
  body: string
}

export type AiContext = {
  bucket_summary: {
    seniority: string | null
    tone: string | null
    availability: string | null
    fields: string[] | null
  } | null
  used_fields: string[] | null
  summary_text: string | null
}

/** `formatDistanceToNow` throws on an unparseable date; a missing due line does not. */
function dueLabel(iso?: string) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return formatDistanceToNow(date, { addSuffix: true })
}

export function MessageApproval() {
  const { data: touchpoints, isLoading } = useGetTouchpointsDue()
  const {mutateAsync:approveTouchpoint}=usePostTouchpointsTouchpointIdApprove()
  const queryClient=useQueryClient()
  const [isBulkApproving, setIsBulkApproving] = useState(false)
  /** Claimed touchpoints, synchronously — see `handleApprove`. */
  const inFlight = useRef(new Set<string>())
  const [editingMessage, setEditingMessage] = useState<string | null>(null)
  const [loadingApprovals, setLoadingApprovals] = useState<Set<string>>(new Set())
  const [expandedContexts, setExpandedContexts] = useState<Set<string>>(new Set())
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set())
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set())

  const [editedTouchpoints, setEditedTouchpoints] = useState<Map<string, Partial<GetTouchpointsDue200Item>>>(new Map())

  function getMostRecentTouchpointsByContactAndBucket(
  touchpoints: GetTouchpointsDue200Item[]
): GetTouchpointsDue200Item[] {

  // group items by contact + bucket
  const groups = new Map<string, GetTouchpointsDue200Item[]>();

  for (const tp of touchpoints) {
    if (!tp.contact_id || !tp.bucket_id) continue;

    const key = `${tp.contact_id}-${tp.bucket_id}`;

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key)!.push(tp);
  }

  const result: GetTouchpointsDue200Item[] = [];

  for (const group of groups.values()) {
    // Sort DESC by created_at (most recent first)
    const sorted = group.sort((a, b) => {
      const dateA = new Date(a.created_at ?? 0).getTime();
      const dateB = new Date(b.created_at ?? 0).getTime();
      return dateB - dateA;
    });

    // Take up to the first 2 items
    result.push(...sorted.slice(0, 2));
  }

  return result;
}


  const editForm = useForm<TouchpointFormData>({
    defaultValues: {
      subject: "",
      body: "",
    },
  })

  const handleApprove = async (touchpoint: GetTouchpointsDue200Item) => {
    const touchpointId = touchpoint.touchpoint_id
    if (!touchpointId) return
    /*
      One draft, one send. The bulk loop and a row's own button can otherwise
      both reach the same touchpoint — the loop is still working through the
      list while the rows stay clickable.

      The lock is a ref rather than the two state sets, because the loop's
      closure holds whatever those were when it started: by the time it reaches
      the fourth draft, `approvedIds` still looks the way it did before the
      first. A ref is read and written synchronously, so the second caller sees
      the first one's claim.
    */
    if (approvedIds.has(touchpointId) || inFlight.current.has(touchpointId)) return
    inFlight.current.add(touchpointId)

    setLoadingApprovals((prev) => new Set(prev).add(touchpointId))

    try {
      const editedData = editedTouchpoints.get(touchpointId)

      const approvalData = {
        subject: editedData?.subject ?? touchpoint.subject as string,
        body: editedData?.body ?? touchpoint.body as string,
        email: touchpoint.contact_email as string,
      }

      await approveTouchpoint({
        data:approvalData,
        touchpointId: touchpoint.touchpoint_id??'',
      })
      /*
        The generated key is ["/touchpoints/due"], and `exact: false` matches by
        prefix *array*, not by string — ["/touchpoints"] is not a prefix of it in
        the sense react-query means, so nothing was ever refetched. The header's
        past-due figure made that visible: it stayed put after an approval.
      */
      queryClient.invalidateQueries({
        queryKey: getGetTouchpointsDueQueryKey(),
        exact: false,
      })

      setApprovedIds((prev) => new Set(prev).add(touchpointId))

      return approvalData
    } finally {
      inFlight.current.delete(touchpointId)
      setLoadingApprovals((prev) => {
        const newSet = new Set(prev)
        newSet.delete(touchpointId)
        return newSet
      })
    }
  }

  const handleReject = (touchpointId: string) => {
    setRejectedIds((prev) => new Set(prev).add(touchpointId))
  }

  const handleEdit = (touchpoint: GetTouchpointsDue200Item) => {
    if (!touchpoint.touchpoint_id) return

    // Get edited values if they exist, otherwise use original
    const editedData = editedTouchpoints.get(touchpoint.touchpoint_id)

    editForm.reset({
      subject: editedData?.subject ?? touchpoint.subject ?? "",
      body: editedData?.body ?? touchpoint.body ?? "",
    })

    setEditingMessage(touchpoint.touchpoint_id)
  }

  const handleSaveEdit = (touchpointId: string) => {
    const formData = editForm.getValues()

    console.log("[v0] Saving edit for touchpoint:", touchpointId, formData)

    // Store the edited data
    setEditedTouchpoints((prev) => {
      const newMap = new Map(prev)
      newMap.set(touchpointId, {
        subject: formData.subject,
        body: formData.body,
      })
      return newMap
    })

    setEditingMessage(null)
  }

  const handleCancelEdit = () => {
    setEditingMessage(null)
    editForm.reset()
  }

  const toggleContext = (touchpointId: string) => {
    setExpandedContexts((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(touchpointId)) {
        newSet.delete(touchpointId)
      } else {
        newSet.add(touchpointId)
      }
      return newSet
    })
  }

  const parseSummaryText = (summaryText?: string) => {
    if (!summaryText) {
      return {
        name: "",
        role: "",
        skills: "",
        experiences: "",
        education: "",
        bucketInfo: "",
      }
    }

    const lines = summaryText.split("\n")
    const candidateLine = lines[0] || ""
    const bucketLine = lines[1] || ""

    const nameMatch = candidateLine.match(/name:\s*([^;]+)/)
    const roleMatch = candidateLine.match(/role:\s*([^;]+)/)
    const skillsMatch = candidateLine.match(/skills:\s*([^;]+)/)
    const experiencesMatch = candidateLine.match(/experiences:\s*([^;]+)/)
    const educationMatch = candidateLine.match(/education:\s*([^;.]+)/)



    return {
      name: nameMatch?.[1]?.trim() || "",
      role: roleMatch?.[1]?.trim() || "",
      skills: skillsMatch?.[1]?.trim() || "",
      experiences: experiencesMatch?.[1]?.trim() || "",
      education: educationMatch?.[1]?.trim() || "",
      bucketInfo: bucketLine,
    }
  }


  const getTouchpointValue = (touchpoint: GetTouchpointsDue200Item, field: "subject" | "body") => {
    if (!touchpoint.touchpoint_id) return touchpoint[field] || ""
    const editedData = editedTouchpoints.get(touchpoint.touchpoint_id)
    return editedData?.[field] ?? touchpoint[field] ?? ""
  }

  const pendingTouchpoints =
    touchpoints?.filter(
      (t) => t.touchpoint_id && !t.is_sent && !approvedIds.has(t.touchpoint_id) && !rejectedIds.has(t.touchpoint_id),
    ) || []

  const processedTouchpoints =
    touchpoints?.filter(
      (t) => t.touchpoint_id && (t.is_sent || approvedIds.has(t.touchpoint_id) || rejectedIds.has(t.touchpoint_id)),
    ) || []

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center space-y-3">
              <Loader2 className="h-8 w-8 mx-auto text-muted-foreground animate-spin" />
              <p className="text-muted-foreground">Loading messages...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const waiting = getMostRecentTouchpointsByContactAndBucket(pendingTouchpoints)
  const highUrgency = waiting.filter((t) => t.urgency === "high").length
  /*
    Only drafts that can actually be sent. `subject`, `body` and `contact_email`
    are all optional on the contract, and a bulk action is the wrong place to
    discover one is missing — those stay for a person to look at.
  */
  const lowUrgency = waiting.filter(
    (t) =>
      t.urgency === "low" &&
      // The edited values are what `handleApprove` sends, so they are what
      // eligibility has to read — a draft edited down to an empty body is not
      // sendable however full the original was.
      getTouchpointValue(t, "subject").trim() &&
      getTouchpointValue(t, "body").trim() &&
      t.contact_email,
  )

  const approveAllLowUrgency = async () => {
    if (isBulkApproving) return
    setIsBulkApproving(true)
    try {
      // One at a time: `forEach` with an async callback fires them all at once
      // and returns before any has landed, so nothing could report a failure.
      for (const touchpoint of lowUrgency) {
        await handleApprove(touchpoint)
      }
    } finally {
      setIsBulkApproving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/*
        17g leads with the size of the queue and how much of it is urgent, and
        offers the one bulk action that is safe: the low-urgency drafts. The
        exec ones stay for a person, which is the whole point of the screen.
      */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b pb-3">
        <span className="font-mono text-[11.5px] text-muted-foreground">
          {waiting.length} waiting
        </span>
        {highUrgency > 0 && (
          <span className="font-mono text-[11.5px] text-attention">
            {highUrgency} high urgency
          </span>
        )}
        {lowUrgency.length > 0 && (
          <button
            type="button"
            onClick={approveAllLowUrgency}
            disabled={isBulkApproving}
            className="ml-auto inline-flex items-center gap-[7px] text-[12px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Check className="size-[13px]" />
            {isBulkApproving
              ? `Sending ${lowUrgency.length}…`
              : `Approve all low urgency`}
          </button>
        )}
      </div>

      <div>
        <h3 className="mb-4 text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Waiting on you
        </h3>
        <div className="space-y-4">
          {waiting.map((touchpoint) => {
            if (!touchpoint.touchpoint_id) return null

            const candidateInfo = parseSummaryText((touchpoint.ai_context as AiContext)?.summary_text??'')
            const fullName = `${touchpoint.first_name || ""} ${touchpoint.last_name || ""}`.trim() || "Unknown"
            const isEditing = editingMessage === touchpoint.touchpoint_id

            return (
              <Card key={touchpoint.touchpoint_id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src="/placeholder.svg" alt={fullName} />
                        <AvatarFallback>
                          {touchpoint.first_name?.[0] || "U"}
                          {touchpoint.last_name?.[0] || ""}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-base">{fullName}</CardTitle>
                        <p className="text-sm text-muted-foreground">{touchpoint.contact_email || "No email"}</p>
                      </div>
                    </div>
                    {/* Plain mono metadata, per 17g — not coloured pills. Only
                        urgency carries a colour, and only when it is high. */}
                    <div className="flex items-baseline gap-4">
                      {(touchpoint.ai_context as unknown as AiContext)?.bucket_summary?.seniority && (
                        <span className="font-mono text-[10.5px] text-muted-foreground">
                          {(touchpoint.ai_context as AiContext).bucket_summary?.seniority}
                        </span>
                      )}
                      {touchpoint.urgency && (
                        <span
                          className={cn(
                            "font-mono text-[10.5px]",
                            touchpoint.urgency === "high"
                              ? "text-attention"
                              : "text-muted-foreground",
                          )}
                        >
                          {touchpoint.urgency}
                        </span>
                      )}
                      {dueLabel(touchpoint.scheduled_for) && (
                        <span className="font-mono text-[10.5px] text-muted-foreground">
                          due {dueLabel(touchpoint.scheduled_for)}
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(touchpoint.ai_context as AiContext) && (
                    <Collapsible>
                      <CollapsibleTrigger
                        className="flex items-center space-x-2 text-sm font-medium hover:text-brand-ink"
                        onClick={() => toggleContext(touchpoint.touchpoint_id!)}
                      >
                        {expandedContexts.has(touchpoint.touchpoint_id) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        <span>AI Generation Context</span>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2">
                        <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg space-y-3 text-sm">
                          {(touchpoint.ai_context as AiContext).bucket_summary && (
                            <div>
                              <h5 className="font-medium text-blue-900 dark:text-blue-100">Bucket Summary</h5>
                              {(touchpoint.ai_context as AiContext).bucket_summary?.seniority && (
                                <p>
                                  <strong>Seniority:</strong> {(touchpoint.ai_context as AiContext).bucket_summary?.seniority}
                                </p>
                              )}
                              {(touchpoint.ai_context as AiContext).bucket_summary?.tone && (
                                <p>
                                  <strong>Tone:</strong> {(touchpoint.ai_context as AiContext).bucket_summary?.tone}
                                </p>
                              )}
                              {(touchpoint.ai_context as AiContext).bucket_summary?.availability && (
                                <p>
                                  <strong>Availability:</strong> {(touchpoint.ai_context as AiContext).bucket_summary?.availability}
                                </p>
                              )}
                              {(touchpoint.ai_context as AiContext).bucket_summary?.fields &&
                                (touchpoint.ai_context as AiContext).bucket_summary?.fields?.length as number > 0 && (
                                  <p>
                                    <strong>Focus Areas:</strong>{" "}
                                    {(touchpoint.ai_context as AiContext).bucket_summary?.fields?.join(", ")}
                                  </p>
                                )}
                            </div>
                          )}

                          {candidateInfo.name && (
                            <div>
                              <h5 className="font-medium text-blue-900 dark:text-blue-100">Candidate Profile</h5>
                              <p>
                                <strong>Name:</strong> {candidateInfo.name}
                              </p>
                              {candidateInfo.role && (
                                <p>
                                  <strong>Role:</strong> {candidateInfo.role}
                                </p>
                              )}
                              {candidateInfo.skills && (
                                <p>
                                  <strong>Skills:</strong> {candidateInfo.skills}
                                </p>
                              )}
                              {candidateInfo.experiences && (
                                <p>
                                  <strong>Experience:</strong> {candidateInfo.experiences}
                                </p>
                              )}
                              {candidateInfo.education && (
                                <p>
                                  <strong>Education:</strong> {candidateInfo.education}
                                </p>
                              )}
                            </div>
                          )}

                          {(touchpoint.ai_context as AiContext).used_fields && (touchpoint.ai_context as AiContext).used_fields?.length as number > 0 && (
                            <div>
                              <h5 className="font-medium text-blue-900 dark:text-blue-100">Fields Used</h5>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(touchpoint.ai_context as AiContext).used_fields?.map((field:any, idx:any) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {(touchpoint.ai_context as AiContext).summary_text && (
                            <div>
                              <h5 className="font-medium text-blue-900 dark:text-blue-100">Summary</h5>
                              <p className="text-xs whitespace-pre-wrap">{(touchpoint.ai_context as AiContext).summary_text}</p>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium">Subject:</Label>
                      {!isEditing && (
                        <Button size="sm" variant="outline" onClick={() => handleEdit(touchpoint)} className="h-7 px-2">
                          <Edit className="h-3 w-3 mr-1" />
                          <span className="text-xs">Edit</span>
                        </Button>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Input {...editForm.register("subject")} className="font-medium" />
                      </div>
                    ) : (
                      <p className="font-medium text-sm bg-accent/50 dark:bg-accent/20 p-3 rounded-md border border-border">
                        {getTouchpointValue(touchpoint, "subject") || "No subject"}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="font-medium mb-2 block">Message Content:</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea {...editForm.register("body")} rows={12} className="font-mono text-sm" />
                        <div className="flex space-x-2">
                          <Button size="sm" onClick={() => handleSaveEdit(touchpoint.touchpoint_id!)}>
                            Save Changes
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-accent/50 dark:bg-accent/20 p-4 rounded-md border border-border">
                        <pre className="whitespace-pre-wrap text-sm font-mono">
                          {getTouchpointValue(touchpoint, "body") || "No content"}
                        </pre>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center space-x-4">
                      {touchpoint.created_at && (
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          Generated: {new Date(touchpoint.created_at).toLocaleString()}
                        </div>
                      )}
                      {touchpoint.scheduled_for && (
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-1" />
                          Scheduled: {new Date(touchpoint.scheduled_for).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {!isEditing && (
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handleApprove(touchpoint)}
                        /* `bg-green-600` is not in this theme's palette, so the
                           button rendered as a card with near-black text on it.
                           Approve is the affirmative action; teal is what the
                           product uses for those. */
                        className="bg-primary text-primary-foreground hover:opacity-90"
                        disabled={loadingApprovals.has(touchpoint.touchpoint_id)}
                      >
                        {loadingApprovals.has(touchpoint.touchpoint_id) ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Sending Email...
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => touchpoint.touchpoint_id && handleReject(touchpoint.touchpoint_id)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                      <Button variant="outline" onClick={() => handleEdit(touchpoint)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>

        {getMostRecentTouchpointsByContactAndBucket(pendingTouchpoints).length === 0 && (
          <Card>
            <CardContent className="flex items-center justify-center h-32">
              <div className="text-center">
                <Mail className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No pending messages for approval</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Activity */}
      {processedTouchpoints.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-2">
            {processedTouchpoints.slice(0, 5).map((touchpoint) => {
              if (!touchpoint.touchpoint_id) return null

              const fullName = `${touchpoint.first_name || ""} ${touchpoint.last_name || ""}`.trim() || "Unknown"
              const status = touchpoint.is_sent
                ? "sent"
                : approvedIds.has(touchpoint.touchpoint_id)
                  ? "approved"
                  : "rejected"

              return (
                <Card key={touchpoint.touchpoint_id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="/placeholder.svg" alt={fullName} />
                          <AvatarFallback className="text-xs">
                            {touchpoint.first_name?.[0] || "U"}
                            {touchpoint.last_name?.[0] || ""}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{fullName}</p>
                          <p className="text-xs text-muted-foreground">
                            {getTouchpointValue(touchpoint, "subject") || "No subject"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant={
                            status === "approved" || status === "sent"
                              ? "default"
                              : status === "rejected"
                                ? "destructive"
                                : "secondary"
                          }
                        >
                          {status}
                        </Badge>
                        {touchpoint.created_at && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(touchpoint.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
